import { supabase } from '@/integrations/supabase/client';

/**
 * Update workbook display order in database (pure database operation)
 */
export async function updateWorkbookDisplayOrder(
  workbookId: string,
  displayOrder: number
): Promise<void> {
  const { error } = await supabase
    .from('workbook')
    .update({ display_order: displayOrder } as any) // Type cast until Supabase types are regenerated
    .eq('id', workbookId);

  if (error) {
    console.error('Failed to update workbook display order:', error);
    throw new Error(`Failed to update workbook order: ${error.message}`);
  }
}

/**
 * Simple hook for workbook ordering - pure local state like File Storage
 */
export function useWorkbookOrderMutations() {
  // Simple async function for single card updates (no React Query)
  const updateSingleOrder = async (update: { itemId: string; newOrder: number }) => {
    console.log('[WorkbookOrderMutations] Single update:', update);
    try {
      await updateWorkbookDisplayOrder(update.itemId, update.newOrder);
      console.log('[WorkbookOrderMutations] Single update completed (no cache invalidation)');
    } catch (error) {
      console.error('[WorkbookOrderMutations] Single update failed:', error);
      throw error; // Re-throw for error handling in calling code
    }
  };

  return {
    updateSingleOrder,
    isUpdating: false, // Simplified - no pending state tracking
  };
}

/**
 * Utility function to initialize display_order for existing workbooks
 * Call this once to populate display_order for workbooks that don't have it
 */
export async function initializeWorkbookDisplayOrders(): Promise<void> {
  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    throw new Error('User must be authenticated');
  }

  // Get workbooks without display_order
  const { data: workbooks, error: fetchError } = await supabase
    .from('workbook')
    .select('id, updated_at')
    .eq('user_id', sessionData.session.user.id)
    .is('display_order', null)
    .order('updated_at', { ascending: false });

  if (fetchError) {
    throw new Error(`Failed to fetch workbooks: ${fetchError.message}`);
  }

  if (!workbooks || workbooks.length === 0) {
    console.log('All workbooks already have display_order set');
    return;
  }

  // Update each workbook with display_order based on position
  const SPACING = 1000;
  const updates = workbooks.map((workbook, index) => ({
    id: workbook.id,
    display_order: (index + 1) * SPACING,
  }));

  for (const update of updates) {
    const { error } = await supabase
      .from('workbook')
      .update({ display_order: update.display_order } as any) // Type cast until Supabase types are regenerated
      .eq('id', update.id);

    if (error) {
      console.error(`Failed to update display_order for workbook ${update.id}:`, error);
    }
  }

  console.log(`Initialized display_order for ${updates.length} workbooks`);
} 